import React from 'react';
import { ArrowLeft, Clock, Eye, Smartphone, TrendingUp } from 'lucide-react';
import { scrollToTop } from '../utils/scrollToTop';

interface TutorialsProps {
  onBack?: () => void;
  onCaptionTutorial?: () => void;
  onVideoEditingTutorial?: () => void;
  onAdvancedComposerTutorial?: () => void;
  onViewAllTools?: () => void;
  onStartAutoCaptions?: () => void;
  onStartVideoEditor?: () => void;
  onStartThumbnail?: () => void;
  onStartVideo?: () => void;
  onStartBanner?: () => void;
  onStartShortsClipper?: () => void;
}

export function Tutorials({ onBack, onCaptionTutorial, onVideoEditingTutorial, 
  onAdvancedComposerTutorial, onViewAllTools, onStartAutoCaptions, onStartVideoEditor,
  onStartThumbnail, onStartVideo, onStartBanner, onStartShortsClipper
}: TutorialsProps) {
  const handleBack = () => {
    if (onBack) return onBack();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="container mx-auto px-4 pt-8">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>
      </div>
      
      <div className="container mx-auto px-4 pb-16">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-6 text-gray-900">
            Video Editing Tutorials
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Master the art of creating viral shorts with our comprehensive tutorials. 
            From basics to advanced techniques, learn everything you need to succeed.
          </p>
        </div>

        {/* Tutorial Categories */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Caption Mastery Tutorial */}
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm flex flex-col h-full">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center">
                <span className="text-3xl">💬</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold">Caption Mastery</h2>
                <p className="text-blue-700">Boost engagement by 40-80%</p>
              </div>
            </div>
            
            <div className="space-y-4 mb-6 flex-1">
              <h3 className="text-xl font-semibold text-blue-700">What You'll Learn:</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                  <span>Auto-transcription setup and optimization</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                  <span>Manual timing adjustments for perfect sync</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                  <span>Style customization and brand consistency</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                  <span>Color psychology for maximum impact</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                  <span>Placement strategies for different platforms</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-500/20 rounded-lg p-4 mb-6">
              <h4 className="font-semibold mb-2 text-blue-700">Pro Tip:</h4>
              <p className="text-base text-gray-600">
                Use contrasting colors and bold fonts. Captions should be readable even without sound, 
                and timing should match the natural rhythm of speech.
              </p>
            </div>

            <div className="flex gap-3 mt-auto">
              <button
                onClick={() => {
                scrollToTop();
                if (onStartAutoCaptions) return onStartAutoCaptions();
                // Fallback to direct navigation
                window.location.href = '/';
              }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                Start Caption Creator
              </button>
              <button 
                onClick={() => {
                  scrollToTop();
                  window.location.href = '/blog';
                }}
                className="px-6 py-3 border border-blue-400 text-blue-700 hover:bg-blue-500/20 rounded-lg transition-colors whitespace-nowrap"
                title="Learn More"
              >
                Learn More
              </button>
            </div>
          </div>

          {/* Video Editing Basics Tutorial */}
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm flex flex-col h-full">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-purple-600 rounded-xl flex items-center justify-center">
                <span className="text-3xl">✂️</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold">Video Editing Basics</h2>
                <p className="text-purple-700">Master the fundamentals</p>
              </div>
            </div>
            
            <div className="space-y-4 mb-6 flex-1">
              <h3 className="text-xl font-semibold text-purple-700">What You'll Learn:</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0"></span>
                  <span>Timeline navigation and precision cutting</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0"></span>
                  <span>Trim techniques for maximum impact</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0"></span>
                  <span>Overlay management and positioning</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0"></span>
                  <span>Audio synchronization and mixing</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0"></span>
                  <span>Export settings for different platforms</span>
                </li>
              </ul>
            </div>

            <div className="bg-purple-500/20 rounded-lg p-4 mb-6">
              <h4 className="font-semibold mb-2 text-purple-700">Pro Tip:</h4>
              <p className="text-base text-gray-600">
                Always cut on action or movement. This creates smoother transitions and keeps 
                viewers engaged. Use the rule of thirds for overlay placement.
              </p>
            </div>

            <div className="flex gap-3 mt-auto">
              <button 
              onClick={() => {
                if (onStartVideoEditor) return onStartVideoEditor();
                // Fallback to direct navigation
                window.location.href = '/';
              }}
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                Start Video Editor
              </button>
              <button 
                onClick={() => {
                  scrollToTop();
                  window.location.href = '/blog';
                }}
                className="px-6 py-3 border border-purple-400 text-purple-700 hover:bg-purple-500/20 rounded-lg transition-colors whitespace-nowrap"
                title="Learn More"
              >
                Learn More
              </button>
            </div>
          </div>

          {/* Advanced Composition Tutorial */}
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm flex flex-col h-full">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-green-600 rounded-xl flex items-center justify-center">
                <span className="text-3xl">🎬</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold">Advanced Composition</h2>
                <p className="text-green-700">Professional editing techniques</p>
              </div>
            </div>
            
            <div className="space-y-4 mb-6 flex-1">
              <h3 className="text-xl font-semibold text-green-700">What You'll Learn:</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></span>
                  <span>Multi-track editing and layering</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></span>
                  <span>Transition effects and timing</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></span>
                  <span>Audio synchronization and mixing</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></span>
                  <span>Color grading and visual effects</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></span>
                  <span>Professional workflow optimization</span>
                </li>
              </ul>
            </div>

            <div className="bg-green-500/20 rounded-lg p-4 mb-6">
              <h4 className="font-semibold mb-2 text-green-700">Pro Tip:</h4>
              <p className="text-base text-gray-600">
                Use the timeline to create visual rhythm. Alternate between fast and slow cuts, 
                and always maintain audio-visual sync for professional results.
              </p>
            </div>

            <div className="flex gap-3 mt-auto">
              <button
                onClick={() => {
                  scrollToTop();
                  window.location.href = '/video-composer';
                }}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                Try Advanced Composer
              </button>
              <button 
                onClick={() => {
                  scrollToTop();
                  window.location.href = '/blog';
                }}
                className="px-6 py-3 border border-green-400 text-green-700 hover:bg-green-500/20 rounded-lg transition-colors whitespace-nowrap"
                title="Learn More"
              >
                Learn More
              </button>
            </div>
          </div>

          {/* Thumbnail Design Tutorial */}
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm flex flex-col h-full">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-orange-600 rounded-xl flex items-center justify-center">
                <span className="text-3xl">🖼️</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold">Thumbnail Design</h2>
                <p className="text-orange-700">Increase click-through rates</p>
              </div>
            </div>
            
            <div className="space-y-4 mb-6 flex-1">
              <h3 className="text-xl font-semibold text-orange-700">What You'll Learn:</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0"></span>
                  <span>Color psychology and emotional impact</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0"></span>
                  <span>Typography hierarchy and readability</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0"></span>
                  <span>Visual balance and composition</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0"></span>
                  <span>Platform-specific optimization</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0"></span>
                  <span>Brand consistency and recognition</span>
                </li>
              </ul>
            </div>

            <div className="bg-orange-500/20 rounded-lg p-4 mb-6">
              <h4 className="font-semibold mb-2 text-orange-700">Pro Tip:</h4>
              <p className="text-base text-gray-600">
                Use high contrast colors and bold, readable fonts. Your thumbnail should tell a story 
                and create curiosity. Test different designs to see what works best for your audience.
              </p>
            </div>

            <div className="flex gap-3 mt-auto">
              <button 
              onClick={() => {
                scrollToTop();
                if (onStartThumbnail) return onStartThumbnail();
                // Fallback to direct navigation
                window.location.href = '/';
              }}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                Design Thumbnail
              </button>
              <button 
                onClick={() => {
                  scrollToTop();
                  window.location.href = '/blog';
                }}
                className="px-6 py-3 border border-blue-400 text-blue-700 hover:bg-blue-500/20 rounded-lg transition-colors whitespace-nowrap"
                title="Learn More"
              >
                Learn More
              </button>
            </div>
          </div>

          {/* Shorts Clipping Tutorial */}
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm flex flex-col h-full">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center">
                <span className="text-3xl">🎯</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold">Shorts Clipping</h2>
                <p className="text-red-700">Maximize content value</p>
              </div>
            </div>
            
            <div className="space-y-4 mb-6 flex-1">
              <h3 className="text-xl font-semibold text-red-700">What You'll Learn:</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                  <span>Auto-highlight detection algorithms</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                  <span>Vertical cropping techniques</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                  <span>Batch processing workflows</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                  <span>Content optimization strategies</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                  <span>Export settings for different platforms</span>
                </li>
              </ul>
            </div>

            <div className="bg-red-500/20 rounded-lg p-4 mb-6">
              <h4 className="font-semibold mb-2 text-red-700">Pro Tip:</h4>
              <p className="text-base text-gray-600">
                Look for moments with high energy, clear audio, and visual impact. 
                Each clip should be self-contained and tell a complete story in under 60 seconds.
              </p>
            </div>

            <div className="flex gap-3 mt-auto">
              <button 
              onClick={() => {
                scrollToTop();
                if (onStartShortsClipper) return onStartShortsClipper();
                // Fallback to direct navigation
                window.location.href = '/';
              }}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                Extract Clips
              </button>
              <button 
                onClick={() => {
                  scrollToTop();
                  window.location.href = '/blog';
                }}
                className="px-6 py-3 border border-blue-400 text-blue-700 hover:bg-blue-500/20 rounded-lg transition-colors whitespace-nowrap"
                title="Learn More"
              >
                Learn More
              </button>
            </div>
          </div>

          {/* Channel Branding Tutorial */}
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm flex flex-col h-full">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-indigo-600 rounded-xl flex items-center justify-center">
                <span className="text-3xl">🎨</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold">Channel Branding</h2>
                <p className="text-indigo-700">Professional channel identity</p>
              </div>
            </div>
            
            <div className="space-y-4 mb-6 flex-1">
              <h3 className="text-xl font-semibold text-indigo-700">What You'll Learn:</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0"></span>
                  <span>Perfect dimensions (2048×1152) for all devices</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0"></span>
                  <span>Brand consistency across platforms</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0"></span>
                  <span>Mobile optimization and safe zones</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0"></span>
                  <span>Typography and visual hierarchy</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0"></span>
                  <span>Color schemes and brand recognition</span>
                </li>
              </ul>
            </div>

            <div className="bg-indigo-500/20 rounded-lg p-4 mb-6">
              <h4 className="font-semibold mb-2 text-indigo-700">Pro Tip:</h4>
              <p className="text-base text-gray-600">
                Design for mobile first - most users will see your banner on their phones. 
                Keep important elements in the center and use high contrast for readability.
              </p>
            </div>

            <div className="flex gap-3 mt-auto">
              <button 
              onClick={() => {
                scrollToTop();
                if (onStartBanner) return onStartBanner();
                // Fallback to direct navigation
                window.location.href = '/';
              }}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                Design Banner
              </button>
              <button 
                onClick={() => {
                  scrollToTop();
                  window.location.href = '/blog';
                }}
                className="px-6 py-3 border border-blue-400 text-blue-700 hover:bg-blue-500/20 rounded-lg transition-colors whitespace-nowrap"
                title="Learn More"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>

        {/* Quick Tips for Success */}
        <div className="bg-blue-600 rounded-2xl p-12 mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Quick Tips for Success</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Keep It Short</h3>
              <p className="text-white">
                Shorts perform best under 60 seconds. Focus on the most engaging moments and cut ruthlessly.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Eye className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Hook Early</h3>
              <p className="text-white">
                Capture attention in the first 3 seconds. Use captions, visual effects, and strong openings.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Vertical First</h3>
              <p className="text-white">
                Design for mobile viewing. Vertical format gets 3x more engagement than horizontal.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Test & Iterate</h3>
              <p className="text-white">
                Try different styles and formats. Analyze what works for your audience and double down on success.
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center bg-green-600 rounded-2xl p-12">
          <h2 className="text-3xl font-bold mb-4">Ready to Master Video Editing?</h2>
          <p className="text-xl text-white mb-8">
            Start with any tutorial and begin creating professional content today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-green-600 px-8 py-4 rounded-lg font-semibold text-xl hover:bg-gray-100 transition-colors">
              Start Learning Now
            </button>
            <button 
              onClick={() => {
                scrollToTop();
                if (onViewAllTools) return onViewAllTools();
                // Fallback to direct navigation
                window.location.href = '/';
              }}
               className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-xl hover:bg-white/10 transition-colors">
              View All Tools
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
